import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useRoles from 'common/hooks/useRoles';
import { NON_SELECTABLE_REVIEW_REASONS, PrescriptionReviewStatus } from 'enums/prescription.enum';
import { capitalize, find, flatten, map, orderBy, size, values, xor } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { markPrescriptionReview } from 'store/thunks/patientThunks';

const ReviewPrescriptionButton = ({
  disabled,
  patientId,
  orderId,
  patientPrescriptions,
  lineItems,
  onSuccess,
}) => {
  const hasAccess = useRoles();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [selectPrescriptionReviewStatus, setSelectPrescriptionReviewStatus] = useState('');

  const prescriptionList = useMemo(() => {
    if (!patientPrescriptions) return [];
    if (patientPrescriptions?.pages) {
      return flatten(values(patientPrescriptions.pages));
    }
    return Array.isArray(patientPrescriptions) ? patientPrescriptions : [];
  }, [patientPrescriptions]);

  const openDialog = () => setShowModal(true);

  const closeDialog = () => {
    setLoading(false);
    setSelectedPrescriptions([]);
    setSelectedLineItems([]);
    setShowModal(false);
    setSelectPrescriptionReviewStatus('');
  };

  const handleCheckboxChange = (prescriptionId, lineItemId) => {
    setSelectedPrescriptions((prevSelected) => xor(prevSelected, [prescriptionId]));
    setSelectedLineItems((prev) => xor(prev, [lineItemId]));
  };

  const handlePrescriptionReview = () => {
    setLoading(true);
    dispatch(
      markPrescriptionReview({
        patientId,
        prescriptionUUIDs: selectedPrescriptions,
        lineItemIds: selectedLineItems,
        reviewTarget: selectPrescriptionReviewStatus,
        orderId,
      })
    )
      .then((result) => {
        if (result) {
          const count = size(selectedPrescriptions);
          const message = `Prescription${count > 1 ? 's' : ''} of the order tagged for review`;
          enqueueSnackbar(message, { variant: 'warning' });
        }
      })
      .finally(() => {
        setSelectPrescriptionReviewStatus('');
        setLoading(false);
        closeDialog();
        onSuccess?.();
      });
  };

  return (
    <>
      <Button
        disabled={disabled || !hasAccess.needsReview}
        variant='outlined'
        color='warning'
        size='small'
        onClick={openDialog}
        sx={{
          px: 1,
          fontSize: '0.75rem',
          minHeight: '30px',
          lineHeight: 'normal',
        }}
      >
        Needs Review
      </Button>

      <Dialog open={showModal} aria-describedby='ActionItemTab-ReviewPrescriptionButton-header'>
        <DialogTitle id='ActionItemTab-ReviewPrescriptionButton-header'>
          Choose a reason for review
        </DialogTitle>
        <DialogContent sx={{ minWidth: 500 }}>
          <>
            <Grid container alignItems='center' justifyContent='space-between' sx={{ mb: 2 }} />
            <Grid container justifyContent='flex-end' marginTop='10px' marginBottom='20px'>
              <TextField
                select
                size='small'
                label='Select Reason'
                value={selectPrescriptionReviewStatus}
                onChange={(e) => setSelectPrescriptionReviewStatus(e.target.value)}
                sx={{ minWidth: 180 }}
                required
              >
                {orderBy(
                  Object.entries(PrescriptionReviewStatus),
                  [([key]) => key.replace(/_/g, ' ')],
                  ['asc']
                )
                  .filter(([, value]) => !NON_SELECTABLE_REVIEW_REASONS.includes(value))
                  .map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {key === PrescriptionReviewStatus.DRUG_PROFILE_REVIEW
                        ? capitalize('DRUG_REVIEW'.replace(/_/g, ' ').toLowerCase())
                        : capitalize(key.replace(/_/g, ' ').toLowerCase())}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            {map(
              lineItems,
              (lineItem) =>
                !lineItem.taggedForReview && (
                  <Grid
                    container
                    key={lineItem.lineItemId}
                    alignItems='center'
                    justifyContent='space-between'
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Grid item>
                      <Typography fontWeight='600'>
                        {capitalize(
                          find(prescriptionList, { prescriptionUUID: lineItem?.prescriptionId })
                            ?.drug ?? lineItem?.itemName
                        )}
                      </Typography>
                    </Grid>

                    <Grid item>
                      <Tooltip
                        title={
                          lineItem.itemType === 'OTC' ? 'OTC item cannot be marked for review' : ''
                        }
                        arrow
                        placement='top'
                      >
                        <div>
                          <Checkbox
                            id={lineItem.prescriptionId}
                            disabled={lineItem.itemType === 'OTC'}
                            size='small'
                            checked={selectedPrescriptions.includes(lineItem.prescriptionId)}
                            onChange={() =>
                              handleCheckboxChange(lineItem.prescriptionId, lineItem.lineItemId)
                            }
                            inputProps={{
                              'aria-label': `mark ${lineItem.prescriptionId} to be review`,
                            }}
                            sx={{ py: 0 }}
                          />
                        </div>
                      </Tooltip>
                    </Grid>
                  </Grid>
                )
            )}
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color='secondary' size='medium' variant='outlined'>
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant='contained'
            onClick={handlePrescriptionReview}
            disabled={!selectedPrescriptions?.length || !selectPrescriptionReviewStatus?.length}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

ReviewPrescriptionButton.propTypes = {
  disabled: PropTypes.bool,
  patientId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  patientPrescriptions: PropTypes.arrayOf(
    PropTypes.shape({
      prescriptionUUID: PropTypes.string,
      drug: PropTypes.string,
    })
  ),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      prescriptionId: PropTypes.string,
    })
  ),
  onSuccess: PropTypes.func,
};

ReviewPrescriptionButton.defaultProps = {
  disabled: false,
  patientPrescriptions: [],
  lineItems: [],
  onSuccess: undefined,
};

export default ReviewPrescriptionButton;
